import { Link } from "react-router-dom";
import { useState } from "react";
import { ArrowRight, Check, Lock, Sparkles, Instagram, Headphones } from "lucide-react";
import SectionReveal from "@/components/SectionReveal";
import HeroNewsletterCard from "@/components/HeroNewsletterCard";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import useSeo from "@/hooks/useSeo";

const HERO_IMG =
  "https://images.leadconnectorhq.com/image/f_webp/q_80/r_1200/u_https://assets.cdn.filesafe.space/ZZ0MV02eR9AkUfkNW9rm/media/68918892600bc50615680821.png";
const PIP_PORTRAIT =
  "https://images.leadconnectorhq.com/image/f_webp/q_80/r_1200/u_https://assets.cdn.filesafe.space/ZZ0MV02eR9AkUfkNW9rm/media/68918892600bc50615680821.png";
const HERO_VIDEO =
  "https://customer-assets.emergentagent.com/job_pip-revamp/artifacts/sv9l3juz_3BA15B55-DFFE-49FF-B59A-F1C92D60184B.mp4";
const STARTER_KIT_VIDEO =
  "https://customer-assets.emergentagent.com/job_pip-revamp/artifacts/u79uzcso_A801FA0A-6349-45CF-980C-7BFE863DDC1C.mp4";

const PATH_CARDS = [
  {
    kind: "free",
    label: "Free Resource",
    title: "Quiet The Noise Starter Kit",
    desc: "A short PDF + guided audio to soften the inner critic and find your first real moments of peace.",
    cta: "Get It Free",
    href: "#lead-magnet",
    accent: "sage",
  },
  {
    kind: "program",
    label: "New Digital Program",
    title: "The Menopause Invitation",
    desc: "Six grounded modules + an interactive workbook that auto-saves your reflections. A different way to meet this transition — with awareness, not anxiety.",
    cta: "Begin — $87",
    href: "/the-menopause-invitation",
    accent: "gold",
    featured: true,
  },
  {
    kind: "waitlist",
    label: "Pre-launch · Waitlist Open",
    title: "The Rewired Life",
    desc: "Four deep-dive modules, morning + evening meditations, and tools to rebuild your relationship with yourself. $197 AUD lifetime access. Pip is recording the final pieces — join the waitlist to be first through the door.",
    cta: "Join the Waitlist",
    href: "/the-rewired-life",
    accent: "sage",
  },
  {
    kind: "free",
    label: "Podcast",
    title: "Pip's Magnificent Midlife Podcast",
    desc: "Conversations on midlife, menopause, identity, relationships and meaningful change.",
    cta: "Listen",
    href: "/podcast",
    accent: "sage",
  },
  {
    kind: "booking",
    label: "1:1 Coaching",
    title: "Personal Coaching Session",
    desc: "Standalone strategic life coaching with clinical hypnotherapy available as part of the toolkit.",
    cta: "Book your first session",
    href: "https://clientportal.zandahealth.com/clientportal/pipbennett",
    accent: "gold",
  },
  {
    kind: "booking",
    label: "1:1 Deep Dive",
    title: "Lasting Transformation",
    desc: "A 6-session package blending strategic life coaching, hypnotherapy, and bespoke psychotherapy work.",
    cta: "Book your first session",
    href: "https://clientportal.zandahealth.com/clientportal/pipbennett",
    accent: "gold",
  },
  {
    kind: "coming",
    label: "Workplace",
    title: "Empower Your Team",
    desc: "Bespoke workshops for workplaces ready to shift from burnout into sustainable performance.",
    cta: "Enquire",
    href: "/contact",
    accent: "sage",
  },
];

const PAIN_POINTS = [
  "You make decisions from fear and external expectations — not from what you actually want.",
  "You start new habits with enthusiasm, then watch yourself quietly abandon them.",
  "The inner critic is relentless — and the 'shoulds' are exhausting to carry.",
  "Deep down you know you're capable of more. You just can't seem to get there — yet.",
];

const TESTIMONIALS = [
  {
    quote:
      "I came to Pip stuck in circular thinking. Six weeks in, I finally made the decision I'd been avoiding for two years. Life feels lighter, clearer, mine.",
    name: "Sarah M.",
    role: "Executive, Sydney",
  },
  {
    quote:
      "Pip is unlike any coach I've worked with. She sees patterns I couldn't see and changes them gently. I feel like myself again — maybe for the first time.",
    name: "Fiona T.",
    role: "The Rewired Life client",
  },
  {
    quote:
      "The meditations alone were worth the price. But the real work is in the modules. Transformative is an overused word. Here it applies.",
    name: "Anna R.",
    role: "The Rewired Life client",
  },
];

function HomeNewsletterForm() {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (loading) return;
    setLoading(true);
    try {
      await api.post("/newsletter", { name, email });
      setDone(true);
      toast.success("Welcome — see you Sunday.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Couldn't sign you up. Try again?");
    } finally {
      setLoading(false);
    }
  };

  if (done)
    return (
      <div className="bg-cream/10 border border-cream/25 rounded-2xl p-8 text-center" data-testid="home-newsletter-success">
        <div className="inline-flex w-12 h-12 items-center justify-center rounded-full bg-gold text-ink mb-3">
          <Check size={20} strokeWidth={1.5} />
        </div>
        <h3 className="font-serif-display text-2xl mb-1">You're in.</h3>
        <p className="text-cream/75 text-sm">See you Sunday morning.</p>
      </div>
    );

  return (
    <form
      onSubmit={submit}
      className="grid grid-cols-1 md:grid-cols-5 gap-3"
      data-testid="home-newsletter-form"
    >
      <input
        required
        placeholder="First name"
        value={name}
        onChange={(e) => setName(e.target.value)}
        className="md:col-span-2 bg-cream/10 border border-cream/25 rounded-full px-5 py-3 text-cream placeholder-cream/50 focus:outline-none focus:border-gold transition"
        data-testid="home-newsletter-name"
      />
      <input
        required
        type="email"
        placeholder="you@yours.com"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        className="md:col-span-2 bg-cream/10 border border-cream/25 rounded-full px-5 py-3 text-cream placeholder-cream/50 focus:outline-none focus:border-gold transition"
        data-testid="home-newsletter-email"
      />
      <button
        type="submit"
        disabled={loading}
        className="bg-gold hover:bg-gold-dark text-ink rounded-full px-6 py-3 font-medium transition-all disabled:opacity-50"
        data-testid="home-newsletter-submit"
      >
        {loading ? "Saving…" : "Subscribe"}
      </button>
    </form>
  );
}

function LeadMagnetForm() {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [downloadUrl, setDownloadUrl] = useState("");
  const [audioUrl, setAudioUrl] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/lead-magnet", { name, email });
      setDownloadUrl(data?.download_url || "/api/starter-kit/download");
      setAudioUrl(data?.audio_url || "/api/audio/quiet-the-noise-meditation.m4a");
      setDone(true);
      toast.success("Your Starter Kit is ready below.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Something went wrong.");
    } finally {
      setLoading(false);
    }
  };

  if (done)
    return (
      <div className="bg-sage/5 border border-sage/20 rounded-2xl p-8 text-center" data-testid="lead-magnet-success">
        <div className="inline-flex w-12 h-12 items-center justify-center rounded-full bg-sage text-cream mb-4">
          <Check size={20} strokeWidth={1.5} />
        </div>
        <h3 className="font-serif-display text-2xl text-ink mb-2">Your Starter Kit is here.</h3>
        <p className="text-ink-soft mb-6">Save these links — you'll also receive an email shortly.</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-w-md mx-auto">
          <a
            href={downloadUrl}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center justify-center gap-2 bg-sage hover:bg-sage-dark text-cream rounded-full px-5 py-3 font-medium text-sm transition-all"
            data-testid="lead-magnet-download-pdf"
          >
            Download PDF
          </a>
          <a
            href={audioUrl}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center justify-center gap-2 border border-sage text-sage hover:bg-sage hover:text-cream rounded-full px-5 py-3 font-medium text-sm transition-all"
            data-testid="lead-magnet-listen-audio"
          >
            Listen to meditation
          </a>
        </div>
        <div className="max-w-md mx-auto mt-6 bg-cream-soft border border-hairline rounded-2xl p-5 text-left">
          <div className="eyebrow mb-3 text-sage text-center">A small note from Pip</div>
          <video
            src={STARTER_KIT_VIDEO}
            controls
            playsInline
            preload="metadata"
            className="w-full rounded-xl bg-ink"
            data-testid="lead-magnet-success-video"
          />
        </div>
      </div>
    );

  return (
    <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-5 gap-3" data-testid="lead-magnet-form" autoComplete="off">
      <input
        required
        type="text"
        name="first_name"
        autoComplete="given-name"
        placeholder="First name"
        value={name}
        onChange={(e) => setName(e.target.value)}
        className="md:col-span-2 bg-white border border-hairline rounded-full px-6 py-4 text-ink placeholder-ink-soft/60 focus:outline-none focus:border-sage transition"
        data-testid="lead-magnet-name"
      />
      <input
        required
        type="email"
        name="email"
        autoComplete="email"
        placeholder="Email address"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        className="md:col-span-2 bg-white border border-hairline rounded-full px-6 py-4 text-ink placeholder-ink-soft/60 focus:outline-none focus:border-sage transition"
        data-testid="lead-magnet-email"
      />
      <button
        type="submit"
        disabled={loading}
        className="bg-sage hover:bg-sage-dark text-cream rounded-full px-6 py-4 font-medium tracking-wide transition-all disabled:opacity-50"
        data-testid="lead-magnet-submit"
      >
        {loading ? "Sending…" : "Send it to me"}
      </button>
    </form>
  );
}

export default function Home() {
  useSeo({
    title: "Pip Bennett — Strategic Life Coach & Clinical Hypnotherapist",
    description: "Live the truth of who you are. Digital coaching programs (The Menopause Invitation $87, The Rewired Life $197) and a free Starter Kit for the person done second-guessing themselves.",
    canonical: "https://pipbennett.com/",
  });
  return (
    <div data-testid="page-home">
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div
          className="absolute inset-0 -z-10"
          style={{ background: "linear-gradient(135deg, #F5EDE4 0%, #E3EBE4 100%)" }}
        />
        <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-14 pt-10 md:pt-16 pb-24 md:pb-36 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <SectionReveal className="lg:col-span-7">
            <div className="eyebrow mb-8" data-testid="hero-eyebrow">
              Pip Bennett · Strategic Life Coach &amp; Clinical Hypnotherapist
            </div>
            <h1
              className="font-serif-display text-5xl md:text-7xl lg:text-[5.2rem] leading-[1.02] text-ink"
              data-testid="hero-title"
            >
              You already know <em className="text-sage italic font-normal">something</em> needs to change.
            </h1>
            <p className="mt-8 max-w-xl text-lg text-ink-soft leading-relaxed">
              I work with capable, responsible, often outwardly successful people who are internally
              stuck — in hesitation, overthinking, or patterns that no longer make sense logically.
              Not therapy. Not symptom management. The work that happens in the space between
              knowing what you want and actually doing it.
            </p>
            <div className="mt-10 flex flex-col sm:flex-row items-start sm:items-center gap-4">
              <Link
                to="/starter-kit"
                data-testid="hero-cta-primary"
                className="inline-flex items-center gap-2 bg-sage hover:bg-sage-dark text-cream rounded-full px-8 py-4 font-medium tracking-wide transition-all shadow-sm hover:shadow-md group"
              >
                Get the free Starter Kit
                <ArrowRight size={16} strokeWidth={1.8} className="group-hover:translate-x-1 transition" />
              </Link>
            </div>
            <HeroNewsletterCard />
          </SectionReveal>

          <SectionReveal delay={200} className="lg:col-span-5">
            <div className="relative">
              <div className="absolute -inset-6 bg-gold/15 rounded-[2rem] -z-10 blur-xl" />
              <video
                src={HERO_VIDEO}
                poster={HERO_IMG}
                controls
                playsInline
                preload="metadata"
                className="w-full rounded-[1.5rem] object-cover shadow-[0_30px_80px_-20px_rgba(74,93,79,0.25)] bg-cream"
                data-testid="hero-video"
              />
            </div>
          </SectionReveal>
        </div>
      </section>

      {/* FEATURED PROGRAM — A Man's Guide to Her Menopause */}
      <section
        className="bg-cream-soft border-y border-hairline py-20 md:py-28"
        data-testid="home-featured-mens-guide"
      >
        <div className="max-w-6xl mx-auto px-6 md:px-10 lg:px-14">
          <SectionReveal>
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-center">
              <div className="lg:col-span-7">
                <div className="eyebrow text-gold-dark mb-4 inline-flex items-center gap-2">
                  <Sparkles size={12} strokeWidth={2} /> Featured Program
                </div>
                <h2 className="font-serif-display text-3xl md:text-4xl lg:text-5xl text-ink leading-[1.08]">
                  A Man's Guide to <em className="italic text-sage">Her Menopause</em>.
                </h2>
                <p className="mt-5 text-ink-soft leading-relaxed max-w-xl">
                  For the man who loves a woman in menopause — and wants to understand
                  what's changing, stop guessing, and show up with more steadiness, care
                  and confidence.
                </p>
                <p className="mt-4 text-ink-soft leading-relaxed max-w-xl">
                  Menopause can change the closeness, the conversations, the intimacy,
                  and the way you understand each other. This practical 6-module program
                  gives him a clear map for moving through it with her — without trying
                  to fix her, withdraw, or get lost in silence.
                </p>
                <div className="mt-8 flex flex-col sm:flex-row gap-3">
                  <Link
                    to="/a-mans-guide-to-her-menopause"
                    className="inline-flex items-center justify-center gap-2 bg-sage hover:bg-sage-dark text-cream rounded-full px-7 py-3.5 font-medium tracking-wide transition-all"
                    data-testid="home-mens-guide-primary-cta"
                  >
                    Explore the program
                    <ArrowRight size={15} strokeWidth={1.8} />
                  </Link>
                  <Link
                    to="/a-mans-guide-to-her-menopause#mens-guide-hero-sample-cta"
                    className="inline-flex items-center justify-center gap-2 bg-cream border border-sage/40 hover:border-sage text-sage hover:text-sage-dark rounded-full px-7 py-3.5 font-medium tracking-wide transition-all"
                    data-testid="home-mens-guide-sample-cta"
                  >
                    Watch Module 1 free
                  </Link>
                </div>
                <p className="mt-4 text-xs text-ink-soft/70">
                  $97 AUD · 6 modules · lifetime access
                </p>
              </div>

              <div className="lg:col-span-5">
                <Link
                  to="/a-mans-guide-to-her-menopause"
                  className="block group"
                  aria-label="Open A Man's Guide to Her Menopause sales page"
                  data-testid="home-mens-guide-image-link"
                >
                  <picture>
                    <source srcSet="/images/mens-guide-hero.webp" type="image/webp" />
                    <img
                      src="/images/mens-guide-hero.png"
                      alt="A Man's Guide to Her Menopause"
                      className="w-full h-auto rounded-3xl shadow-[0_24px_60px_-25px_rgba(74,93,79,0.35)] group-hover:shadow-[0_30px_70px_-25px_rgba(74,93,79,0.5)] transition-shadow"
                      loading="lazy"
                    />
                  </picture>
                </Link>
              </div>
            </div>
          </SectionReveal>
        </div>
      </section>

      {/* CAUGHT IN THE NOISE */}
      <section className="bg-peach/70 py-24 md:py-36">
        <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-14 grid grid-cols-1 lg:grid-cols-12 gap-16 items-center">
          <SectionReveal className="lg:col-span-5">
            <img
              src={PIP_PORTRAIT}
              alt="Pip"
              className="w-full rounded-[1.5rem] object-cover"
              data-testid="caught-image"
            />
          </SectionReveal>
          <SectionReveal delay={150} className="lg:col-span-7">
            <div className="eyebrow mb-6 text-sage">Sound Familiar?</div>
            <h2 className="font-serif-display text-4xl md:text-5xl lg:text-6xl text-sage leading-[1.05] hairline">
              Caught in the Noise.
            </h2>
            <p className="mt-10 text-lg text-ink leading-relaxed max-w-xl">
              You've read the books. Done the programs. Tried the tools. And yet something keeps pulling you back
              to the same patterns, the same frustration, the same quiet question:{" "}
              <em className="text-sage">why isn't this working?</em>
            </p>
            <ul className="mt-10 space-y-5 max-w-xl">
              {PAIN_POINTS.map((p, i) => (
                <li key={i} className="flex items-start gap-4">
                  <span className="mt-1.5 flex-shrink-0 w-2 h-2 rounded-full bg-sage" />
                  <span className="text-ink leading-relaxed">{p}</span>
                </li>
              ))}
            </ul>
            <Link
              to="/the-menopause-invitation"
              data-testid="caught-cta"
              className="mt-10 inline-flex items-center gap-2 bg-gold hover:bg-gold-dark text-ink rounded-full px-8 py-4 font-medium tracking-wide transition-all"
            >
              Here's What's Actually Missing
              <ArrowRight size={16} strokeWidth={1.8} />
            </Link>
          </SectionReveal>
        </div>
      </section>

      {/* CHOOSE YOUR PATH FORWARD */}
      <section className="py-24 md:py-36" data-testid="path-forward">
        <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-14">
          <SectionReveal>
            <div className="eyebrow mb-4 text-sage">Work With Pip</div>
            <h2 className="font-serif-display text-4xl md:text-5xl lg:text-6xl text-ink leading-[1.05] max-w-3xl">
              Choose your path forward.
            </h2>
            <p className="mt-6 text-ink-soft max-w-2xl leading-relaxed">
              Whether you're beginning gently with a free resource, diving into the signature program, or waiting for
              a 1:1 place — there's a door that fits where you are right now.
            </p>
          </SectionReveal>

          <div className="mt-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {PATH_CARDS.map((c, i) => {
              const isWaitlist = c.kind === "waitlist";
              return (
                <SectionReveal key={c.title} delay={i * 80}>
                  <div
                    className={`relative h-full rounded-2xl overflow-hidden border transition-all duration-300 ${
                      c.featured
                        ? "bg-sage text-cream border-sage shadow-[0_30px_60px_-20px_rgba(74,93,79,0.35)] hover:shadow-[0_40px_80px_-20px_rgba(74,93,79,0.45)]"
                        : isWaitlist
                        ? "bg-cream-soft border-hairline"
                        : "bg-white border-hairline hover:shadow-[0_20px_40px_-20px_rgba(74,93,79,0.15)]"
                    }`}
                    data-testid={`path-card-${c.title.toLowerCase().replace(/\s+/g, "-")}`}
                  >
                    {isWaitlist && (
                      <span className="absolute top-6 right-6 bg-gold text-ink text-[10px] font-bold uppercase tracking-widest px-3 py-1.5 rounded-full inline-flex items-center gap-1.5">
                        <Lock size={10} strokeWidth={2} /> Books Closed
                      </span>
                    )}
                    {c.featured && (
                      <span className="absolute top-6 right-6 bg-gold text-ink text-[10px] font-bold uppercase tracking-widest px-3 py-1.5 rounded-full inline-flex items-center gap-1.5">
                        <Sparkles size={10} strokeWidth={2} /> Most Popular
                      </span>
                    )}
                    <div className="p-8 md:p-10 flex flex-col h-full min-h-[280px]">
                      <div
                        className={`text-[10px] font-semibold uppercase tracking-[0.2em] mb-5 ${
                          c.featured ? "text-gold" : "text-sage"
                        }`}
                      >
                        {c.label}
                      </div>
                      <h3
                        className={`font-serif-display text-2xl md:text-[1.7rem] leading-tight mb-4 ${
                          c.featured ? "text-cream" : "text-ink"
                        }`}
                      >
                        {c.title}
                      </h3>
                      <p className={`text-sm leading-relaxed mb-8 flex-1 ${c.featured ? "text-cream/80" : "text-ink-soft"}`}>
                        {c.desc}
                      </p>
                      {(() => {
                        const isExternal = /^https?:\/\//.test(c.href);
                        const isAnchor = c.href.startsWith("#");
                        const ctaClass = `inline-flex items-center gap-2 text-sm font-medium tracking-wide transition group ${
                          c.featured ? "text-gold hover:text-cream" : isWaitlist ? "text-gold hover:text-gold-dark" : "text-sage hover:text-sage-dark"
                        }`;
                        const ctaTestId = `path-card-cta-${c.title.toLowerCase().replace(/\s+/g, "-")}`;
                        const ctaInner = (
                          <>
                            {c.cta}
                            <ArrowRight size={14} strokeWidth={1.8} className="group-hover:translate-x-1 transition" />
                          </>
                        );
                        if (isExternal) {
                          return (
                            <a
                              href={c.href}
                              target="_blank"
                              rel="noopener noreferrer"
                              className={ctaClass}
                              data-testid={ctaTestId}
                            >
                              {ctaInner}
                            </a>
                          );
                        }
                        return (
                          <Link
                            to={isAnchor ? "/" : c.href}
                            onClick={(e) => {
                              if (isAnchor) {
                                e.preventDefault();
                                document.querySelector(c.href)?.scrollIntoView({ behavior: "smooth" });
                              }
                            }}
                            className={ctaClass}
                            data-testid={ctaTestId}
                          >
                            {ctaInner}
                          </Link>
                        );
                      })()}
                    </div>
                  </div>
                </SectionReveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* NEWSLETTER — moved up for visibility */}
      <section id="newsletter" className="py-24 md:py-28 bg-cream-soft">
        <div className="max-w-5xl mx-auto px-6 md:px-10">
          <SectionReveal>
            <div className="bg-sage text-cream rounded-[2rem] p-10 md:p-16 shadow-sm relative overflow-hidden">
              <div className="absolute -top-20 -right-20 w-80 h-80 bg-gold/10 rounded-full blur-3xl" />
              <div className="relative text-center">
                <div className="eyebrow mb-5 text-gold">Free weekly newsletter · Sunday with Pip</div>
                <h2 className="font-serif-display text-4xl md:text-5xl lg:text-[3.4rem] leading-[1.05]">
                  One quiet email, every Sunday.
                </h2>
                <p className="mt-6 max-w-xl mx-auto text-cream/80 leading-relaxed">
                  No sales pitch. No to-do list. Just one observation, one small invitation, and a reminder that
                  you're allowed to slow down. Written by Pip the night before, in real time.
                </p>
                <div className="mt-10 max-w-xl mx-auto">
                  <HomeNewsletterForm />
                </div>
                <p className="mt-5 text-xs text-cream/55">
                  Join a growing community of people who start their Sunday with Pip.
                </p>
              </div>
            </div>
          </SectionReveal>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="py-24 md:py-36">
        <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-14">
          <SectionReveal>
            <div className="eyebrow mb-4 text-sage">Client Stories</div>
            <h2 className="font-serif-display text-4xl md:text-5xl lg:text-6xl text-ink max-w-3xl leading-[1.05]">
              Quiet change. Lasting difference.
            </h2>
          </SectionReveal>
          <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-6">
            {TESTIMONIALS.map((t, i) => (
              <SectionReveal key={i} delay={i * 120}>
                <div className="bg-white border border-hairline rounded-2xl p-8 md:p-10 h-full flex flex-col">
                  <div className="font-serif-display text-5xl text-gold leading-none mb-4">"</div>
                  <p className="text-ink leading-relaxed flex-1">{t.quote}</p>
                  <div className="mt-8 pt-6 border-t border-hairline">
                    <div className="font-medium text-ink text-sm">{t.name}</div>
                    <div className="text-xs text-ink-soft mt-1">{t.role}</div>
                  </div>
                </div>
              </SectionReveal>
            ))}
          </div>
        </div>
      </section>

      {/* FREE RESOURCES */}
      <section id="lead-magnet" className="bg-cream-soft py-24 md:py-32 border-y border-hairline">
        <div className="max-w-6xl mx-auto px-6 md:px-10">
          <SectionReveal>
            <div className="text-center mb-14 md:mb-16">
              <div className="eyebrow mb-5 text-sage">Free Resources</div>
              <h2 className="font-serif-display text-4xl md:text-5xl lg:text-[3.4rem] text-ink leading-[1.05]">
                Free resources for clearer thinking and <em className="italic text-sage">meaningful change</em>.
              </h2>
              <p className="mt-6 max-w-2xl mx-auto text-ink-soft leading-relaxed">
                Whether you are feeling overwhelmed by the noise in your head or beginning to question whether
                the life you are living still fits, these free resources offer a practical place to begin.
              </p>
            </div>
          </SectionReveal>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
            <SectionReveal>
              <article
                className="h-full flex flex-col bg-white border border-hairline rounded-2xl p-8 md:p-10"
                data-testid="home-free-resource-starter-kit"
              >
                <div className="w-12 h-12 rounded-full inline-flex items-center justify-center bg-sage/10 text-sage mb-6">
                  <Headphones size={20} strokeWidth={1.5} />
                </div>
                <div className="eyebrow mb-3 text-sage">Workbook + Guided Meditation</div>
                <h3 className="font-serif-display text-2xl md:text-[1.7rem] text-ink leading-tight mb-4">
                  Quiet the Noise Starter Kit
                </h3>
                <p className="text-ink-soft leading-relaxed mb-6 flex-1">
                  A short PDF and 10-minute guided meditation to soften the inner critic, recognise your noise,
                  and find your first real moments of peace.
                </p>
                <Link
                  to="/starter-kit"
                  className="inline-flex items-center justify-center gap-2 bg-sage hover:bg-sage-dark text-cream rounded-full px-6 py-4 font-medium tracking-wide transition-all"
                  data-testid="home-free-resource-starter-kit-cta"
                >
                  Get the Quiet the Noise Starter Kit
                  <ArrowRight size={16} strokeWidth={1.8} />
                </Link>
              </article>
            </SectionReveal>

            <SectionReveal delay={100}>
              <article
                className="h-full flex flex-col bg-white border border-hairline rounded-2xl p-8 md:p-10"
                data-testid="home-free-resource-clarity-guide"
              >
                <div className="w-12 h-12 rounded-full inline-flex items-center justify-center bg-gold/15 text-gold-dark mb-6">
                  <Sparkles size={20} strokeWidth={1.5} />
                </div>
                <div className="eyebrow mb-3 text-gold-dark">15-Minute Reflection Guide · New</div>
                <h3 className="font-serif-display text-2xl md:text-[1.7rem] text-ink leading-tight mb-4">
                  Something Has to Change: A Midlife Clarity Guide
                </h3>
                <p className="text-ink-soft leading-relaxed mb-6 flex-1">
                  A short reflective guide for anyone who can sense that something about the way they are living
                  is no longer working — even when nothing is obviously wrong.
                </p>
                <Link
                  to="/midlife-clarity-guide"
                  className="inline-flex items-center justify-center gap-2 bg-ink hover:bg-ink/90 text-cream rounded-full px-6 py-4 font-medium tracking-wide transition-all"
                  data-testid="home-free-resource-clarity-guide-cta"
                >
                  Get the Free Clarity Guide
                  <ArrowRight size={16} strokeWidth={1.8} />
                </Link>
              </article>
            </SectionReveal>
          </div>

          <SectionReveal>
            <div className="mt-12 text-center">
              <Link
                to="/free-resources"
                className="inline-flex items-center gap-2 text-sage hover:text-sage-dark text-sm font-medium tracking-wide transition"
                data-testid="home-free-resources-see-all"
              >
                See all free resources
                <ArrowRight size={14} strokeWidth={1.8} />
              </Link>
            </div>
          </SectionReveal>
        </div>
      </section>

      {/* PODCAST + INSTAGRAM */}
      <section className="py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-14 grid grid-cols-1 md:grid-cols-2 gap-6">
          <SectionReveal>
            <a
              href="https://open.spotify.com/show/0VaUEuK7KOrBv19THWGM2v"
              target="_blank"
              rel="noreferrer"
              className="group block bg-ink text-cream rounded-2xl p-12 md:p-14 h-full relative overflow-hidden hover:scale-[1.005] transition-transform"
              data-testid="podcast-card"
            >
              <Headphones size={28} strokeWidth={1.3} className="text-gold mb-6" />
              <div className="eyebrow mb-4 text-gold">The Podcast</div>
              <h3 className="font-serif-display text-3xl md:text-4xl leading-tight">Pip&rsquo;s Magnificent Midlife Podcast</h3>
              <p className="mt-4 text-cream/70 max-w-md leading-relaxed">
                Conversations on midlife, menopause, identity, relationships and meaningful change.
              </p>
              <div className="mt-8 inline-flex items-center gap-2 text-gold text-sm font-medium">
                Listen on Spotify
                <ArrowRight size={14} strokeWidth={1.8} className="group-hover:translate-x-1 transition" />
              </div>
            </a>
          </SectionReveal>
          <SectionReveal delay={120}>
            <a
              href="https://www.instagram.com/pausewithpip/"
              target="_blank"
              rel="noreferrer"
              className="group block bg-peach/60 text-ink rounded-2xl p-12 md:p-14 h-full hover:scale-[1.005] transition-transform border border-hairline"
              data-testid="instagram-card"
            >
              <Instagram size={28} strokeWidth={1.3} className="text-sage mb-6" />
              <div className="eyebrow mb-4 text-sage">Instagram</div>
              <h3 className="font-serif-display text-3xl md:text-4xl leading-tight">@pausewithpip</h3>
              <p className="mt-4 text-ink-soft max-w-md leading-relaxed">
                Quiet reflections, behavioural insights, and reminders that your patterns can change.
              </p>
              <div className="mt-8 inline-flex items-center gap-2 text-sage text-sm font-medium">
                Follow along
                <ArrowRight size={14} strokeWidth={1.8} className="group-hover:translate-x-1 transition" />
              </div>
            </a>
          </SectionReveal>
        </div>
      </section>
    </div>
  );
}
